import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Header from './Header';
import Footer from './Footer';
import './ProfilePage.css';


const ProfilePage = () => {
  const { user, avatarUrl, setAvatarUrl } = useUser();
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [activeSection, setActiveSection] = useState('personal');
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeError, setResumeError] = useState('');
  const [resumeSuccess, setResumeSuccess] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const avatarInputRef = React.useRef(null);

  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    linkedIn: '',
    github: '',
    portfolio: '',
    jobTitle: '',
    yearsExperience: '',
    professionalSummary: '',
    skills: '',
    certifications: '',
    workAuthorization: '',
    salaryExpectation: '',
    willingToRelocate: false,
    desiredEmploymentType: '',
    workExperience: [{ company: '', position: '', duration: '', description: '' }],
    education: [{ institution: '', degree: '', field: '', year: '' }],
    projects: [{ name: '', description: '', technologies: '' }],
  });

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data.profile) {
        setProfile({
          fullName: data.profile.fullName || '',
          email: data.profile.email || user?.email || '',
          phone: data.profile.phone || '',
          location: data.profile.location || '',
          linkedIn: data.profile.linkedIn || '',
          github: data.profile.github || '',
          portfolio: data.profile.portfolio || '',
          jobTitle: data.profile.jobTitle || '',
          yearsExperience: data.profile.yearsExperience || '',
          professionalSummary: data.profile.professionalSummary || '',
          skills: data.profile.skills || '',
          certifications: data.profile.certifications || '',
          workAuthorization: data.profile.workAuthorization || '',
          salaryExpectation: data.profile.salaryExpectation || '',
          willingToRelocate: data.profile.willingToRelocate || false,
          desiredEmploymentType: data.profile.desiredEmploymentType || '',
          workExperience:
            data.profile.workExperience?.length > 0
              ? data.profile.workExperience
              : [{ company: '', position: '', duration: '', description: '' }],
          education:
            data.profile.education?.length > 0
              ? data.profile.education
              : [{ institution: '', degree: '', field: '', year: '' }],
          projects:
            data.profile.projects?.length > 0
              ? data.profile.projects
              : [{ name: '', description: '', technologies: '' }],
        });
      } else {
        setProfile((prev) => ({ ...prev, email: user?.email || '' }));
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResumeReupload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];
    if (!allowed.includes(file.type)) {
      setResumeError('Please upload a PDF or DOCX file');
      return;
    }

    setResumeUploading(true);
    setResumeError('');
    setResumeSuccess('');

    const formData = new FormData();
    formData.append('resume', file);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/profile/parse-resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Upload failed');

      // Merge parsed data into current profile (keep fields that aren't returned)
      setProfile(prev => ({
        ...prev,
        ...data.data,
        workExperience: data.data.workExperience?.length
          ? data.data.workExperience
          : prev.workExperience,
        education: data.data.education?.length
          ? data.data.education
          : prev.education,
        projects: data.data.projects?.length
          ? data.data.projects
          : prev.projects,
        resumeFilename: data.resumeFilename || prev.resumeFilename,
      }));

      setResumeSuccess('✅ Resume parsed! Review the updated fields below, then click Save Changes.');
    } catch (err) {
      setResumeError(err.message);
    } finally {
      setResumeUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarUploading(true);
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/profile/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Upload failed');
      setAvatarUrl(data.avatarUrl);
    } catch (err) {
      setMessage({ text: err.message, type: 'error' });
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!profile.fullName?.trim()) {
      setMessage({ text: 'Full name is required.', type: 'error' });
      return;
    }
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/profile/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profile),
      });
      const data = await res.json();

      if (res.ok) {
        setMessage({ text: '✅ Profile saved successfully!', type: 'success' });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
      } else {
        setMessage({ text: data.message || 'Failed to save profile.', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Network error. Please try again.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) =>
    setProfile((p) => ({ ...p, [field]: value }));

  const updateArrayItem = (arrayName, index, field, value) => {
    setProfile((p) => {
      const arr = [...p[arrayName]];
      arr[index] = { ...arr[index], [field]: value };
      return { ...p, [arrayName]: arr };
    });
  };

  const addArrayItem = (arrayName, template) =>
    setProfile((p) => ({ ...p, [arrayName]: [...p[arrayName], template] }));

  const removeArrayItem = (arrayName, index) =>
    setProfile((p) => ({
      ...p,
      [arrayName]: p[arrayName].filter((_, i) => i !== index),
    }));

  const sections = [
    { id: 'personal', label: '👤 Personal', icon: '👤' },
    { id: 'summary', label: '📝 Summary', icon: '📝' },
    { id: 'experience', label: '💼 Experience', icon: '💼' },
    { id: 'education', label: '🎓 Education', icon: '🎓' },
    { id: 'skills', label: '⚡ Skills', icon: '⚡' },
    { id: 'projects', label: '🚀 Projects', icon: '🚀' },
  ];

  if (loading) {
    return (
    <div className="neural-glass-homepage">
                <div className="neural-background"></div> 
                <div className="geometric-shapes"> 
                <div className="shape shape-1"></div>
                <div className="shape shape-2"></div>
                <div className="shape shape-3"></div>
                <div className="shape shape-4"></div>
                </div>

        <div className="pp-page">
            <Header setShowAuthModal={() => {}} />
            <div className="pp-loading">
            <div className="pp-spinner" />
            <p>Loading your profile…</p>
            </div>
            <Footer />
        </div>
    </div>
    );
  }

  return (
    <div className="neural-glass-homepage">
        <div className="neural-background"></div> 
        <div className="geometric-shapes"> 
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
        </div>

        <div className="pp-page">
        <Header setShowAuthModal={() => {}} />

        <div className="pp-hero">
            <div className="pp-hero-inner">
            <div
              className={`pp-avatar pp-avatar--clickable ${avatarUploading ? 'pp-avatar--uploading' : ''}`}
              onClick={() => !avatarUploading && avatarInputRef.current?.click()}
              title="Click to change photo"
            >
              {avatarUrl
                ? <img src={`${API_BASE_URL}${avatarUrl}`} alt="avatar" className="pp-avatar-img" />
                : (profile.fullName ? profile.fullName[0].toUpperCase() : user?.name?.[0]?.toUpperCase() || 'U')
              }
              <div className="pp-avatar-overlay">{avatarUploading ? '…' : '📷'}</div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
            </div>
            <div className="pp-hero-info">
                <h1 className="pp-hero-name">{profile.fullName || user?.name || 'Your Name'}</h1>
                <p className="pp-hero-title">{profile.jobTitle || 'Add your job title'}</p>
                <p className="pp-hero-email">{profile.email || user?.email}</p>
            </div>
            <button
                className={`pp-save-btn ${saving ? 'pp-save-btn--loading' : ''}`}
                onClick={handleSave}
                disabled={saving}
            >
                {saving ? 'Saving…' : 'Save Changes'}
            </button>
            </div>
        </div>

        {message.text && (
            <div className={`pp-toast pp-toast--${message.type}`}>{message.text}</div>
        )}

        <div className="pp-layout">
            {/* Sidebar Nav */}
            <nav className="pp-sidebar">
            {sections.map((s) => (
                <button
                key={s.id}
                className={`pp-nav-item ${activeSection === s.id ? 'pp-nav-item--active' : ''}`}
                onClick={() => setActiveSection(s.id)}
                >
                <span className="pp-nav-icon">{s.icon}</span>
                <span className="pp-nav-label">{s.label.replace(/^\S+ /, '')}</span>
                </button>
            ))}
            </nav>

            {/* Content */}
            <main className="pp-content">

            {/* ── PERSONAL ── */}
            {activeSection === 'personal' && (
              <Section title="Personal Information">

                {/* ── Resume Re-upload ── */}
                <div className="pp-resume-upload-box">
                  

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.doc"
                    onChange={handleResumeReupload}
                    style={{ display: 'none' }}
                  />

                  <button
                    className={`pp-resume-upload-btn ${resumeUploading ? 'pp-resume-upload-btn--loading' : ''}`}
                    onClick={() => !resumeUploading && fileInputRef.current?.click()}
                    disabled={resumeUploading}
                  >
                    {resumeUploading ? (
                      <>
                        <span className="pp-resume-spinner" /> Parsing resume with AI...
                      </>
                    ) : (
                      '⬆ Upload New Resume'
                    )}
                  </button>

                  {resumeError && (
                    <p className="pp-resume-error">⚠️ {resumeError}</p>
                  )}
                  {resumeSuccess && (
                    <p className="pp-resume-success">{resumeSuccess}</p>
                  )}
                </div>

                <div className="pp-grid">
                    <Field label="Full Name *" value={profile.fullName} onChange={(v) => updateField('fullName', v)} />
                    <Field label="Email" value={profile.email} onChange={(v) => updateField('email', v)} type="email" />
                    <Field label="Phone" value={profile.phone} onChange={(v) => updateField('phone', v)} placeholder="+1 (555) 000-0000" />
                    <Field label="Location" value={profile.location} onChange={(v) => updateField('location', v)} placeholder="City, Country" />
                    <Field label="Job Title" value={profile.jobTitle} onChange={(v) => updateField('jobTitle', v)} placeholder="e.g. Software Engineer" />
                    <Field label="Years of Experience" value={profile.yearsExperience} onChange={(v) => updateField('yearsExperience', v)} placeholder="e.g. 5" />
                    <Field label="LinkedIn URL" value={profile.linkedIn} onChange={(v) => updateField('linkedIn', v)} placeholder="https://linkedin.com/in/..." />
                    <Field label="GitHub URL" value={profile.github} onChange={(v) => updateField('github', v)} placeholder="https://github.com/..." />
                    <Field label="Portfolio URL" value={profile.portfolio} onChange={(v) => updateField('portfolio', v)} placeholder="https://yoursite.com" />
                </div>

                <div style={{ marginTop: '24px' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(224,163,255,0.7)', marginBottom: '12px' }}>
                    Job Application Preferences
                  </p>
                  <div className="pp-grid">
                    <SelectField label="Work Authorization" value={profile.workAuthorization} onChange={(v) => updateField('workAuthorization', v)}
                      options={['US Citizen','Green Card','H1B Visa','OPT / CPT','TN Visa','E3 Visa','Require Sponsorship']} />
                    <SelectField label="Preferred Employment Type" value={profile.desiredEmploymentType} onChange={(v) => updateField('desiredEmploymentType', v)}
                      options={['Full-time','Contract (W2)','Contract (C2C)','Contract (1099)','Part-time','Any']} />
                    <Field label="Salary / Rate Expectation" value={profile.salaryExpectation} onChange={(v) => updateField('salaryExpectation', v)} placeholder="e.g. $80/hr or $120k/year" />
                    <div className="pp-field">
                      <label className="pp-label">Willing to Relocate</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px' }}>
                        <div onClick={() => updateField('willingToRelocate', !profile.willingToRelocate)}
                          style={{ width: '40px', height: '22px', borderRadius: '11px', background: profile.willingToRelocate ? 'rgba(74,222,128,0.5)' : 'rgba(255,255,255,0.15)', position: 'relative', cursor: 'pointer', transition: 'background 0.2s' }}>
                          <div style={{ position: 'absolute', top: '3px', left: profile.willingToRelocate ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: profile.willingToRelocate ? '#4ade80' : 'rgba(255,255,255,0.5)', transition: 'left 0.2s' }} />
                        </div>
                        <span style={{ fontSize: '0.85rem', opacity: 0.7 }}>{profile.willingToRelocate ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>
                </div>
                </Section>
            )}

            {/* ── SUMMARY ── */}
            {activeSection === 'summary' && (
                <Section title="Professional Summary">
                <p className="pp-hint">A brief overview of your professional background and goals.</p>
                <TextArea
                    value={profile.professionalSummary}
                    onChange={(v) => updateField('professionalSummary', v)}
                    rows={6}
                    placeholder="Write a concise summary of your experience, skills, and what you bring to the table…"
                />
                </Section>
            )}

            {/* ── EXPERIENCE ── */}
            {activeSection === 'experience' && (
                <Section
                title="Work Experience"
                action={
                    <button
                    className="pp-add-btn"
                    onClick={() =>
                        addArrayItem('workExperience', { company: '', position: '', duration: '', description: '' })
                    }
                    >
                    + Add Position
                    </button>
                }
                >
                {profile.workExperience.map((exp, i) => (
                    <SubCard
                    key={i}
                    label={exp.company ? `${exp.position || 'Position'} @ ${exp.company}` : `Experience #${i + 1}`}
                    onRemove={profile.workExperience.length > 1 ? () => removeArrayItem('workExperience', i) : null}
                    >
                    <div className="pp-grid">
                        <Field label="Company" value={exp.company} onChange={(v) => updateArrayItem('workExperience', i, 'company', v)} />
                        <Field label="Position / Title" value={exp.position} onChange={(v) => updateArrayItem('workExperience', i, 'position', v)} />
                        <Field label="Duration" value={exp.duration} onChange={(v) => updateArrayItem('workExperience', i, 'duration', v)} placeholder="Jan 2022 – Present" />
                    </div>
                    <TextArea
                        value={exp.description}
                        rows={4}
                        onChange={(v) => updateArrayItem('workExperience', i, 'description', v)}
                        placeholder="Key responsibilities, achievements, technologies used…"
                    />
                    </SubCard>
                ))}
                </Section>
            )}

            {/* ── EDUCATION ── */}
            {activeSection === 'education' && (
                <Section
                title="Education"
                action={
                    <button
                    className="pp-add-btn"
                    onClick={() =>
                        addArrayItem('education', { institution: '', degree: '', field: '', year: '' })
                    }
                    >
                    + Add Degree
                    </button>
                }
                >
                {profile.education.map((edu, i) => (
                    <SubCard
                    key={i}
                    label={edu.institution || `Education #${i + 1}`}
                    onRemove={profile.education.length > 1 ? () => removeArrayItem('education', i) : null}
                    >
                    <div className="pp-grid">
                        <Field label="Institution" value={edu.institution} onChange={(v) => updateArrayItem('education', i, 'institution', v)} />
                        <Field label="Degree" value={edu.degree} onChange={(v) => updateArrayItem('education', i, 'degree', v)} placeholder="e.g. Bachelor of Science" />
                        <Field label="Field of Study" value={edu.field} onChange={(v) => updateArrayItem('education', i, 'field', v)} placeholder="e.g. Computer Science" />
                        <Field label="Year" value={edu.year} onChange={(v) => updateArrayItem('education', i, 'year', v)} placeholder="2018 – 2022" />
                    </div>
                    </SubCard>
                ))}
                </Section>
            )}

            {/* ── SKILLS ── */}
            {activeSection === 'skills' && (
                <Section title="Skills & Certifications">
                <Field
                    label="Skills"
                    value={profile.skills}
                    onChange={(v) => updateField('skills', v)}
                    placeholder="JavaScript, React, Node.js, Python, AWS…"
                />
                <div className="pp-skills-preview">
                    {profile.skills
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((skill, i) => (
                        <span key={i} className="pp-skill-chip">{skill}</span>
                    ))}
                </div>
                <div style={{ marginTop: '28px' }}>
                    <Field
                    label="Certifications"
                    value={profile.certifications}
                    onChange={(v) => updateField('certifications', v)}
                    placeholder="AWS Certified Developer, Google Analytics, PMP…"
                    />
                </div>
                </Section>
            )}

            {/* ── PROJECTS ── */}
            {activeSection === 'projects' && (
                <Section
                title="Projects"
                action={
                    <button
                    className="pp-add-btn"
                    onClick={() =>
                        addArrayItem('projects', { name: '', description: '', technologies: '' })
                    }
                    >
                    + Add Project
                    </button>
                }
                >
                {profile.projects.map((proj, i) => (
                    <SubCard
                    key={i}
                    label={proj.name || `Project #${i + 1}`}
                    onRemove={profile.projects.length > 1 ? () => removeArrayItem('projects', i) : null}
                    >
                    <div className="pp-grid">
                        <Field label="Project Name" value={proj.name} onChange={(v) => updateArrayItem('projects', i, 'name', v)} />
                        <Field label="Technologies" value={proj.technologies} onChange={(v) => updateArrayItem('projects', i, 'technologies', v)} placeholder="React, Node.js, MongoDB…" />
                    </div>
                    <TextArea
                        value={proj.description}
                        rows={3}
                        onChange={(v) => updateArrayItem('projects', i, 'description', v)}
                        placeholder="What you built, the problem it solved, and its impact…"
                    />
                    </SubCard>
                ))}
                </Section>
            )}

            {/* Bottom Save */}
            <div className="pp-bottom-save">
                {message.text && (
                <span className={`pp-inline-msg pp-inline-msg--${message.type}`}>{message.text}</span>
                )}
                <button
                className={`pp-save-btn ${saving ? 'pp-save-btn--loading' : ''}`}
                onClick={handleSave}
                disabled={saving}
                >
                {saving ? 'Saving…' : 'Save Changes'}
                </button>
            </div>

            </main>
        </div>

        <Footer />
        </div>

    </div>
  );
};

// ── Helper Components ──────────────────────────────────────────────────────

const Section = ({ title, children, action }) => (
  <div className="pp-section">
    <div className="pp-section-header">
      <h2 className="pp-section-title">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

const SubCard = ({ label, children, onRemove }) => (
  <div className="pp-subcard">
    <div className="pp-subcard-header">
      <span className="pp-subcard-label">{label}</span>
      {onRemove && (
        <button className="pp-remove-btn" onClick={onRemove}>✕ Remove</button>
      )}
    </div>
    {children}
  </div>
);

const Field = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div className="pp-field">
    <label className="pp-field-label">{label}</label>
    <input
      type={type}
      value={value || ''}
      placeholder={placeholder || ''}
      onChange={(e) => onChange(e.target.value)}
      className="pp-input"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div className="pp-field">
    <label className="pp-field-label">{label}</label>
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="pp-input">
      <option value="">Select...</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const TextArea = ({ value, onChange, rows, placeholder }) => (
  <textarea
    value={value || ''}
    rows={rows || 3}
    placeholder={placeholder || ''}
    onChange={(e) => onChange(e.target.value)}
    className="pp-textarea"
  />
);

export default ProfilePage;